import React from "react"
import { Box, Stack, Link, Paper, useTheme, Container, SvgIcon } from "@carrotfertility/carotene-core"
import { Link as RouterLink } from "react-router-dom"

function CarrotDot(): JSX.Element {
  return (
    <Box position="absolute" display="flex" left="0" right="0" justifyContent="center">
      <Box
        height="8rem"
        width="8rem"
        display="flex"
        alignItems="center"
        marginTop="-4rem"
        sx={(theme) => ({
          background: theme.palette.primary.main,
          color: theme.palette.common.white,
          borderRadius: theme.tokens.borderRadius.round,
          padding: theme.spacing(theme.tokens.spacing.md)
        })}
      >
        <SvgIcon viewBox="0 0 131 21" sx={{ fontSize: "6rem" }}>
          <path d="M 0 10.0029L 0 10.0591C 0 15.613 4.2002 20.0061 9.83691 20.0061C 13.457 20.0061 15.667 18.6799 17.6846 16.5801L 15.501 14.3701C 13.8438 15.917 12.3242 16.884 9.94727 16.884C 6.24512 16.884 3.56445 13.7891 3.56445 10.0029L 3.56445 9.94897C 3.56445 6.16309 6.24512 3.12305 9.94727 3.12305C 12.1582 3.12305 13.8164 4.09009 15.3633 5.5271L 17.5459 3.01294C 15.6953 1.24512 13.54 0 9.97461 0C 4.11719 0 0 4.50488 0 10.0029ZM 24.6035 19.6753L 31.1523 4.25635L 37.7012 19.6753L 41.293 19.6753L 32.7822 0.193359L 29.6318 0.193359L 21.1211 19.6753L 24.6035 19.6753ZM 54.6387 9.92017C 57.0703 9.92017 58.6172 8.64917 58.6172 6.68823L 58.6172 6.63232C 58.6172 4.5603 57.126 3.42725 54.6113 3.42725L 49.6641 3.42725L 49.6641 9.92017L 54.6387 9.92017ZM 46.2656 0.332275L 54.8877 0.332275C 57.3193 0.332275 59.2256 1.05127 60.4697 2.26733C 61.4912 3.31616 62.0713 4.75317 62.0713 6.43921L 62.0713 6.49438C 62.0713 9.67236 60.165 11.5784 57.457 12.3523L 62.6797 19.6753L 58.6729 19.6753L 53.9209 12.9333L 53.8652 12.9333L 49.665 12.9333L 49.665 19.6753L 46.2656 19.6753L 46.2656 0.332275ZM 81.5312 6.68823C 81.5312 8.64917 79.9844 9.92017 77.5527 9.92017L 72.5781 9.92017L 72.5781 3.42725L 77.5254 3.42725C 80.04 3.42725 81.5312 4.5603 81.5312 6.63232L 81.5312 6.68823ZM 77.8018 0.332275L 69.1807 0.332275L 69.1807 19.6753L 72.5791 19.6753L 72.5791 12.9333L 76.7793 12.9333L 76.835 12.9333L 81.5879 19.6753L 85.5938 19.6753L 80.3721 12.3523C 83.0801 11.5784 84.9863 9.67236 84.9863 6.49438L 84.9863 6.43921C 84.9863 4.75317 84.4062 3.31616 83.3838 2.26733C 82.1396 1.05127 80.2334 0.332275 77.8018 0.332275ZM 106.622 10.0591L 106.622 10.0029C 106.622 6.24609 103.887 3.12305 100.046 3.12305C 96.2051 3.12305 93.5234 6.19116 93.5234 9.94897L 93.5234 10.0029C 93.5234 13.762 96.2598 16.884 100.101 16.884C 103.942 16.884 106.622 13.8171 106.622 10.0591ZM 89.96 10.0591L 89.96 10.0029C 89.96 4.56006 94.1602 0 100.101 0C 106.042 0 110.186 4.50488 110.186 9.94897L 110.186 10.0029C 110.186 15.448 105.986 20.0061 100.046 20.0061C 94.1035 20.0061 89.96 15.502 89.96 10.0591ZM 114.804 3.48267L 120.938 3.48267L 120.938 19.6748L 124.365 19.6748L 124.365 3.48267L 130.5 3.48267L 130.5 0.332764L 114.804 0.332764L 114.804 3.48267Z" />
        </SvgIcon>
      </Box>
    </Box>
  )
}

type AppAccessPageCardProps = {
  shake?: boolean
  id?: string
}

export function AppAccessPageCard({
  shake,
  children,
  id
}: React.PropsWithChildren<AppAccessPageCardProps>): JSX.Element {
  return (
    <Container
      component="main"
      id={id}
      sx={{
        position: "relative"
      }}
      className={shake ? "shake" : ""}
    >
      <CarrotDot />
      <Paper
        sx={(theme) => ({
          borderRadius: theme.tokens.borderRadius.md,
          padding: theme.spacing("6rem", theme.tokens.spacing.xxxl, theme.tokens.spacing.xl),
          [theme.breakpoints.down("md")]: {
            padding: theme.spacing("4.5rem", theme.tokens.spacing.lg, theme.tokens.spacing.lg, theme.tokens.spacing.lg)
          }
        })}
      >
        {children}
      </Paper>
    </Container>
  )
}

type AppAccessPageCardFooterLink = {
  id: string
  to: string
  linkText: string
  external?: boolean
}
interface AppAccessPageCardFooterProps {
  links: AppAccessPageCardFooterLink[]
}

export function AppAccessPageCardFooter(props: AppAccessPageCardFooterProps): JSX.Element {
  const theme = useTheme()
  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.lg)} alignItems="center">
      {props.links.map((linkProps) =>
        linkProps.external ? (
          <Link key={linkProps.id} id={linkProps.id} href={linkProps?.to}>
            {linkProps?.linkText}
          </Link>
        ) : (
          <Link key={linkProps.id} component={RouterLink} id={linkProps.id} to={linkProps?.to}>
            {linkProps?.linkText}
          </Link>
        )
      )}
    </Stack>
  )
}
